import React, { FC, useState, ChangeEvent, useContext } from 'react';
import { Order, SubOrderWarning, SubOrderWarningKind } from 'models/order';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { declineDialogTemplate } from 'apollo/templates/order';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';

type DeclineByLabDialogProps = {
    onUpdate?: (order: Order) => void;
    onClose: () => void;
    open: boolean;
    order?: Order;
};

const DeclineByLabDialog: FC<DeclineByLabDialogProps> = ({
    onUpdate,
    onClose,
    open,
    order,
}: DeclineByLabDialogProps) => {
    const { t: translate } = useTranslation(['orders']);
    const [message, setMessage] = useState<string>('');
    // DD-3482 Marketplace: offered only when the order has sub-contractor orders.
    const [cancelSubOrders, setCancelSubOrders] = useState<boolean>(false);
    const hasSubOrders = Boolean(order?.subOrders?.length);
    const { showBoundary } = useDenthubErrorBoundary();
    const { setAppToasterContent } = useContext(AppToasterContext);
    const handleMessageBox = (event: ChangeEvent<HTMLInputElement>) => {
        setMessage(event.target.value);
    };

    const [changeStatus, { loading: isDeclining }] = useMutation(declineDialogTemplate, {
        onCompleted: ({ declineOrder }) => {
            onClose();
            onUpdate?.(declineOrder);
            // DD-3482: the order is declined either way; a sub-order the backend could not
            // cancel stays active and is listed in the warning banner on the order.
            const stillActive = (declineOrder?.subOrderWarnings ?? []).filter(
                (warning: SubOrderWarning) => warning.kind === SubOrderWarningKind.SUB_ORDER_STILL_ACTIVE,
            );
            if (cancelSubOrders && stillActive.length) {
                setAppToasterContent({
                    isOpen: true,
                    severity: 'warning',
                    message: translate('orders:SUB_ORDERS_NOT_CANCELLED', { count: stillActive.length }),
                });
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleChangeStatus = async () => {
        await changeStatus({
            variables: {
                orderId: order?._id,
                declineMessage: message,
                cancelSubOrders: hasSubOrders ? cancelSubOrders : undefined,
            },
        });
    };

    return (
        <DHDialog
            fullWidth
            maxWidth="md"
            open={open}
            onClose={onClose}
            title={translate('orders:DECLINE_THE_ORDER')}
            actions={
                <Button
                    disabled={!message || isDeclining}
                    sx={{ minWidth: 250 }}
                    variant="contained"
                    onClick={handleChangeStatus}
                    data-test="decline-order-dialog.decline-btn"
                >
                    {translate('orders:DECLINE_THE_ORDER')}
                </Button>
            }
        >
            <Box mb={1}>
                <Typography variant="h4" mb={2}>
                    {translate('orders:WHY_ARE_YOU_DECLINING_THE_ORDER')}
                </Typography>
                <TextField
                    fullWidth
                    minRows={6}
                    multiline
                    placeholder={translate('orders:ADD_AN_EXPLANATION')}
                    value={message}
                    onChange={handleMessageBox}
                    data-test="decline-order-dialog.message-input"
                />
            </Box>
            <Box mb={3}>
                <Alert severity="warning" sx={{ whiteSpace: 'pre-wrap' }}>
                    {translate('orders:ARE_YOU_SURE_THAT_YOU_WANT_TO_DECLINE_THE_ORDER')}
                </Alert>
            </Box>
            {hasSubOrders && (
                <Box mb={3}>
                    <Alert severity="warning">{translate('orders:ORDER_HAS_SUB_CONTRACTOR_ORDERS')}</Alert>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={cancelSubOrders}
                                onChange={(event) => setCancelSubOrders(event.target.checked)}
                                data-test="decline-order-dialog.cancel-sub-orders-checkbox"
                            />
                        }
                        label={translate('orders:DO_YOU_ALSO_WANT_TO_CANCEL_THE_SUB_CONTRACTOR_ORDERS')}
                    />
                </Box>
            )}
        </DHDialog>
    );
};
export default DeclineByLabDialog;
